import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'
import { error, log } from '@/services/Log'
import { QueryParamsType } from '@/types'
import { onFilterChangeHandler, onPaginationHandler, SortValueValueType } from '@/components/Plp/types'
import { PLP_Items_Per_Page } from '@/data/constants'
const DefaultSort = 'bestSelling'


const removeDuplicateParams: (s: string) => string = (path) => {
  const split = path.split('?')
  const base = split[0] || ''
  const params = split[1] || ''
  const uniqueParams = [...(
    new Set(params.split('&'))
  )]
  let value = `${base}?${uniqueParams.join('&')}`.replace(/\&$/, '').replace(/\?$/, '')
  return value
}

interface PLPActionsProps {
  queryParam: QueryParamsType,
  baseHref: string,
  baseUrl: string,
  itemsPerPage: number
}

const usePLPActions: (p: PLPActionsProps) => {
  onSortChange: (value: SortValueValueType) => void
  isLoading: boolean,
  sortValue: SortValueValueType,
  onFilterSelected: onFilterChangeHandler,
  onClearAllFilters: () => void
  onPageChange: onPaginationHandler
  setItemsPerPage: (items: number) => Promise<void>
} = ({
  queryParam,
  baseHref,
  baseUrl,
  itemsPerPage,
}) => {
    const router = useRouter()

    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [sortValue, setSortValue] = useState<SortValueValueType>(queryParam['sort'] as SortValueValueType || DefaultSort)
    const [limit, setLimit] = useState<number>(itemsPerPage)

    useEffect(() => setLimit(itemsPerPage), [itemsPerPage])

    useEffect(() => {
      setSortValue(queryParam['sort'] as SortValueValueType || DefaultSort)
    }, [queryParam['sort']])

    const clearQueryParam = (query: string, param: string) => {
      return `${query || ''}`.replace(new RegExp(`((\\&*)${param}=([a-z0-9]*)$|${param}=([a-z0-9]*)(\\&))`, 'i'), '')
    }

    const setQueryParam = useCallback<(query: string, param: string, value: string | number) => string>((query, param, value) => {
      let newQuery = clearQueryParam(query, param)
      const shouldAddAmpersand = !!(`${newQuery || ''}`.replace(/^\?/, '')).length
      return newQuery + `${shouldAddAmpersand ? '&' : ''}${param}=${value}`
    }, [])

    const setQueryPage = useCallback<(s: string, p?: string) => string>((query, pageNumber = '1') => {
      if (pageNumber !== '1' && pageNumber) {
        return setQueryParam(query, 'page', pageNumber)
      }
      return clearQueryParam(query, 'page')
    }, [setQueryParam])

    const combinePaths: (base: string, params: string) => string = (base, params) => {
      if (base.includes('?')) {
        params = params.replace('?', '')
      }
      if ((base.endsWith('&') || base.endsWith('?')) && params.startsWith('&')) {
        params = params.slice(1)
      }
      let path = base + params
      return path
    }
    const getLinksFromQuery = useCallback<(q: string, s?: string) => { href: string, url: string }>((query, s = sortValue) => {
      query = setQueryParam(query, 'sort', s)
      if (limit !== PLP_Items_Per_Page && !query.includes('limit=')) {
        query = setQueryParam(query, 'limit', limit)
      }
      query = query.replace(/(^\&|\&$)/, '')
      const href = removeDuplicateParams(combinePaths(baseHref, query))
      const url = removeDuplicateParams(combinePaths(baseUrl, query))
      return { href, url }
    }, [sortValue, limit, setQueryParam])

    const onSortChange = useCallback(async (value: SortValueValueType) => {
      log('onSortChange Start', { value })
      if (isLoading) {
        error('Method Called while loading')
        return
      }
      setIsLoading(true)
      try {
        console.log(`Handle Sort Change ${value}`)
        if (queryParam['sort'] === value) {
          throw new Error('Sort already selected')
        }
        setSortValue(value)
        const isNotQuery = (p: string) => !p.startsWith('query')
        const queryParams = window.location.href.split('?')?.[1] || ''
        const hasSort = !!queryParams?.includes('sort')
        let query = `${window.location.search}`.replace(/^\?/, '').split('&').filter(isNotQuery).join('&')
        if (hasSort) {
          query = query.replace(/(\&*)sort\=([a-z0-9]*)/gi, '')
        }
        query = setQueryPage(query)
        const { href, url } = getLinksFromQuery(query, value)
        log(`router.push( "${href}", "${url}" )`)
        await router.push(href, url)
      } catch (err) {
        error('SortChange error', { err })
      }
      setIsLoading(false)
      log('onSortChange End')
    }, [isLoading, queryParam, baseHref, baseUrl, getLinksFromQuery, setQueryPage])

    const onFilterSelected: onFilterChangeHandler = useCallback(async (facet, option) => {
      log('onFilterSelected Start', { facet, option })
      if (isLoading) {
        error('Method Called while loading')
        return
      }
      setIsLoading(true)
      try {
        let query = setQueryPage(option.action)
        const { href, url } = getLinksFromQuery(query)
        log(`router.push( "${href}", "${url}" )`)
        await router.push(href, url)
      } catch (err) {
        error(`Failed to select filter ${err}`)
      }
      setIsLoading(false)
      log('onFilterSelected End')
    }, [isLoading, setIsLoading, queryParam, baseHref, baseUrl, getLinksFromQuery, setQueryPage])

    const onClearAllFilters = useCallback(async () => {
      log('onClearAllFilters start')
      if (isLoading) {
        error('Method Called while loading')
        return
      }
      setIsLoading(true)
      try {
        const { href, url } = getLinksFromQuery('')
        log(`router.push( "${href}", "${url}" )`)
        await router.push(href, url)
      } catch (err) {
        error(`onClearAllFilters Failed to clear all filter ${err}`)
      }
      setIsLoading(false)
      log('onClearAllFilters end')
    }, [isLoading, setIsLoading, baseHref, baseUrl, getLinksFromQuery])

    const onPageChange: onPaginationHandler = useCallback(async ({ pageSelected }) => {
      log('onPageChange start', { pageSelected })
      if (isLoading) {
        error('Method Called while loading')
        return
      }
      setIsLoading(true)
      try {
        let originalQuery = window.location.search.replace(/^\?/, '')
        let query = setQueryPage(originalQuery, `${pageSelected}`)
        // const { href, url } = getAppliedFilterUrls({ slug, legacyId, query, isSearch, queryParam })
        const { href, url } = getLinksFromQuery(query)
        log(`router.push( "${href}", "${url}" )`)
        await router.push(href, url)
      } catch (err) {
        error(`Failed to clear all filter ${err}`)
      }
      setIsLoading(false)
      log('onPageChange end')
    }, [isLoading, setIsLoading, baseHref, baseUrl, getLinksFromQuery, setQueryPage])

    const setItemsPerPage = useCallback(async (newLimit: number) => {
      if (isLoading) {
        error('setItemsPerPage Method Called while loading')
        return
      }
      log('setItemsPerPage start', { newLimit })
      setIsLoading(true)
      try {
        let originalQuery = window.location.search.replace(/^\?/, '')
        let query = clearQueryParam(originalQuery, 'limit')
        query = clearQueryParam(query, 'page')
        query = setQueryParam(query, 'limit', newLimit)
        console.log(`Setting items per page to ${newLimit}`)
        setLimit(newLimit)
        const { href, url } = getLinksFromQuery(query)
        log(`router.push( "${href}", "${url}" )`)

        await router.push(href, url)
      } catch (err) {
        error(`Set items per page limit failed ${err}`)
      }

      setIsLoading(false)
      log('setItemsPerPage end')
    }, [isLoading, setIsLoading, getLinksFromQuery, baseHref, baseUrl])

    return {
      isLoading,
      sortValue,
      onSortChange,
      onFilterSelected,
      onClearAllFilters,
      onPageChange,
      setItemsPerPage,
    }
  }

export default usePLPActions

